import * as mjmlBrowserModule from 'mjml-browser';

// mjml-browser v5 moved to an async API: mjml2html returns Promise<{html, errors}>
// instead of the synchronous shape v4 returned. Callers must await the
// result; this util's signature is also Promise-returning so the iframe
// composer can drive it from a useEffect rather than useMemo.
//
// The v5 bundle still ships as webpack-UMD with no `__esModule` marker, so
// vite's esbuild CJS-to-ESM interop can resolve `import mjml2html from
// 'mjml-browser'` to either the function or a namespace whose `default` is
// the function (depending on the build). Recover both shapes defensively.
type MjmlOptions = {
  validationLevel?: 'strict' | 'soft' | 'skip';
  keepComments?: boolean;
  minify?: boolean;
};
type Mjml2HtmlFn = (
  mjml: string,
  options?: MjmlOptions,
) => Promise<{ html: string; errors: unknown[] }>;
const ns = mjmlBrowserModule as unknown as {
  default?: Mjml2HtmlFn;
} & Mjml2HtmlFn;
const mjml2html: Mjml2HtmlFn = typeof ns.default === 'function' ? ns.default : ns;

// Matches `<section ... data-render-mode="live" ...>...</section>`. Captures
// the attributes on either side of the data-render-mode marker so the wrapper
// element (with data-section-name, etc.) survives intact when we splice the
// compiled body back in. Inner content is captured separately for MJML
// compile.
const LIVE_SECTION_RE =
  /<section\b([^>]*?)\bdata-render-mode\s*=\s*["']live["']([^>]*)>([\s\S]*?)<\/section\s*>/gi;

// Cheap pre-check so the common case (no live sections at all) skips the
// regex-replace + per-match mjml compile entirely.
const LIVE_SECTION_DETECT_RE = /\bdata-render-mode\s*=\s*["']live["']/i;

/**
 * Compile any `<section data-render-mode="live">` blocks in the chat iframe's
 * body HTML by wrapping their inner MJML in `<mjml><mj-body>` and running
 * mjml-browser. The original `<section>` wrapper (with `data-section-name`,
 * any other data-* attrs) is preserved verbatim — only the inner body bytes
 * get swapped for the compiled HTML.
 *
 * Mirrors the backend slicer's interleave step (which produces
 * `templates.compiled_html`). Without this client-side compile, the iframe
 * would show raw `<mj-section>` / `<mj-text>` / `<mj-button>` tags as
 * unknown elements — they collapse inline with no styles and the live
 * section looks unstyled even though the final compiled output is fine.
 *
 * Sync by design: the chat surface composes the iframe HTML inside a
 * useMemo, and mjml-browser is synchronous (no DOM, no async fetches).
 * Compile failures fall through to the raw match so the iframe still renders
 * something readable mid-stream when the agent emits a half-finished MJML
 * subtree.
 */
/**
 * Compile a bare MJML body fragment (`<mj-section>...`) to spliceable HTML in
 * the browser: the compiled <body> innerHTML with the <head>'s <style> tags
 * prepended inline. MJML inlines static styles onto each table cell, but
 * media queries can't be inlined — the responsive breakpoint that promotes
 * `.mj-column-per-50` from `width:100%` (mobile-stacked default) to
 * `width:50%` at (min-width:480px) lives only in <head>. Drop it and a 2-up
 * product row stays stacked even at 600px. Browsers honor `<style>` in body,
 * so inlining the extracted styles at the top of the fragment is safe and
 * scopes per-section. Returns null on compile failure so callers can fall
 * back to whatever they were showing.
 *
 * Shared by the chat-iframe live-section splicer below and the editor's
 * SectionLivePreview (the in-canvas "what will live MJML look like" pane).
 */
export async function compileMjmlFragmentInBrowser(innerMjml: string): Promise<string | null> {
  try {
    const wrapped = `<mjml><mj-body width="600px">${innerMjml}</mj-body></mjml>`;
    const compiled = await mjml2html(wrapped, {
      validationLevel: 'soft',
      keepComments: false,
      minify: false,
    });
    const compiledHtml = compiled?.html ?? '';
    const bodyMatch = compiledHtml.match(/<body\b[^>]*>([\s\S]*)<\/body>/i);
    const bodyInner = bodyMatch ? bodyMatch[1] : compiledHtml;
    const headMatch = compiledHtml.match(/<head\b[^>]*>([\s\S]*?)<\/head>/i);
    const headInner = headMatch ? headMatch[1] : '';
    const styleTags = headInner.match(/<style\b[\s\S]*?<\/style>/gi) ?? [];
    return styleTags.join('') + bodyInner;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[compile-live-sections] mjml compile failed:', err);
    return null;
  }
}

// Walk the body HTML and compile each `<section data-render-mode="live">`
// in parallel. We can't use String.prototype.replace with an async callback
// because the resulting strings come back as `Promise<string>` and replace
// joins them as-is. Instead: collect matches up front, compile their MJML
// concurrently, then splice the resolved HTML back in by index.
export async function compileLiveSectionsInHtml(html: string): Promise<string> {
  if (!html || !LIVE_SECTION_DETECT_RE.test(html)) return html;
  const matches: Array<{
    start: number;
    end: number;
    attrsBefore: string;
    attrsAfter: string;
    innerMjml: string;
  }> = [];
  let m: RegExpExecArray | null;
  // exec loop is intentional — String.replaceAll's callback can't be async
  // and we want to collect spans up front so the splice below stays sync.
  while ((m = LIVE_SECTION_RE.exec(html)) !== null) {
    matches.push({
      start: m.index,
      end: m.index + m[0].length,
      attrsBefore: m[1],
      attrsAfter: m[2],
      innerMjml: m[3],
    });
  }
  if (matches.length === 0) return html;

  const compiledBodies = await Promise.all(
    matches.map(({ innerMjml }) => compileMjmlFragmentInBrowser(innerMjml)),
  );

  // Splice in reverse so earlier offsets stay valid as we mutate the tail.
  let out = html;
  for (let i = matches.length - 1; i >= 0; i--) {
    const match = matches[i];
    const bodyInner = compiledBodies[i];
    if (!bodyInner) continue;
    const before = out.slice(0, match.start);
    const after = out.slice(match.end);
    const replacement = `<section${match.attrsBefore}data-render-mode="live"${match.attrsAfter}>${bodyInner}</section>`;
    out = before + replacement + after;
  }
  return out;
}
